import { useEffect } from 'react';
import letsChatSVG from '../assets/lets_chat_icon.svg';
import ChatConfig from '@microsoft/omnichannel-chat-sdk/lib/core/ChatConfig';
import sdkPackageInfo from '@microsoft/omnichannel-chat-sdk/package.json';
import chatWidgetPackageInfo from '@microsoft/omnichannel-chat-widget/package.json';
import chatComponentPackageInfo from '@microsoft/omnichannel-chat-components/package.json';
import type { LiveChatWidgetActionType } from '@microsoft/omnichannel-chat-widget/lib/types/contexts/common/LiveChatWidgetActionType';

import { useChatContextStore } from '@microsoft/omnichannel-chat-widget';
import { useChatContext } from 'globals/context/ccs-chat-provider';
import { ccsChatLocalisedTexts } from './ccs-chat-localised-texts';
import closeSVG from '../assets/close_chat_icon.svg';
import minimiseSVG from '../assets/minimise_icon.svg';

import LogLevel from '@microsoft/omnichannel-chat-sdk/lib/telemetry/LogLevel';
import { IChatSDKLogger } from '@microsoft/omnichannel-chat-widget/lib/types/common/telemetry/interfaces/IChatSDKLogger';
import { TelemetryInput } from '@microsoft/omnichannel-chat-widget/lib/types/common/telemetry/TelemetryConstants';
import { DatadogClient } from '@workivate/tho-web-shared';

export const customChatLogger = (): IChatSDKLogger => {
  const customLogger: IChatSDKLogger = {
    log: (logLevel: LogLevel, telemetryInput: TelemetryInput): void => {
      const payload =
        telemetryInput?.payload &&
        Object.keys(telemetryInput?.payload).length > 0
          ? telemetryInput?.payload
          : '';

      if (logLevel === LogLevel.ERROR) {
        const error = new Error('[TMHChatSDK] Error');

        DatadogClient.addError(error);
        DatadogClient.logMessage(error.message, {
          payload,
        });
      }
    },
    dispose: (): void => {},
  };
  return customLogger;
};

export const telemetryConfig = {
  disableConsoleLog: true,
  chatWidgetVersion: chatWidgetPackageInfo.version,
  chatComponentVersion: chatComponentPackageInfo.version,
  OCChatSDKVersion: sdkPackageInfo.version,
  telemetryLoggers: [customChatLogger()],
};

type Props = {
  chatLocale: string;
};

const CustomHeaderIcon = ({ chatLocale }: Props) => {
  const [_, dispatch] = useChatContextStore();
  const { ccsWebChatLoaded } = useChatContext();

  const localizedTexts = ccsChatLocalisedTexts(chatLocale);

  useEffect(() => {
    dispatch({
      type: 2 as LiveChatWidgetActionType.SET_MIDDLEWARE_LOCALIZED_TEXTS,
      payload: localizedTexts.middleWareLocalisedTexts,
    });
  }, [ccsWebChatLoaded]);

  return (
    <div className='header-icon'>
      <img alt='' src={letsChatSVG} />
    </div>
  );
};

const CustomMinimizeButton = () => {
  const { setCCSChatContainerVisible } = useChatContext();

  return (
    <button
      className='ccs-chat-header-btn'
      onClick={() => {
        setCCSChatContainerVisible(false);

        const chatButton = document.querySelector(
          '#ccs-chat-button',
        ) as HTMLElement;

        if (chatButton) chatButton.focus();
      }}
    >
      <img
        alt={polyglot.t('ccs_chat.chat_container.minimise_chat')}
        src={minimiseSVG}
      />
    </button>
  );
};

export const outOfOfficeHoursProps = (chatConfig: ChatConfig) => {
  return {
    controlProps: {
      titleText:
        chatConfig['LiveChatConfigOperatingHoursSettings']?.[
          'msdyn_offlinetitle'
        ],
    },
    styleProps: {
      generalStyleProps: {
        minWidth: '300px',
        minHeight: '400px',
        padding: '10px',
      },
    },
  };
};

export const headerProps = (chatLocale: string) => ({
  controlProps: {
    hideCloseButton: false,
    closeButtonProps: {
      className: 'ccs-chat-header-btn',
      imageIconProps: {
        alt: polyglot.t('ccs_chat.chat_container.close_chat'),
        src: closeSVG,
      },
    },
  },
  componentOverrides: {
    headerIcon: <CustomHeaderIcon chatLocale={chatLocale} />,
    headerMinimizeButton: <CustomMinimizeButton />,
  },
});

export const webChatContainerProps = {
  // throws Type '{ showAvatarInGroup: string; }' is not assignable to type 'StyleOptions' even though it exists, other options work fine
  // webChatStyles: {
  //   showAvatarInGroup: 'status', // stops from rendering an image on every message
  // },
  // webChatProps: {
  //   locale: getUserLocale(), // doesn't work
  // },
  disableNewLineMarkdownSupport: true,
  disableMarkdownMessageFormatting: true, //setting the default to true for a known issue with markdown
};

export const footerProps = {
  controlProps: {
    hideDownloadTranscriptButton: true,
    hideEmailTranscriptButton: true,
    // hideAudioNotificationButton: true, // library_bug?: this will disable notification sound if hidden - hidden with css instead
  },
};
